<script setup>
    import { ref, onMounted, computed, onUnmounted } from 'vue'

    import Reminder from '@/components/Reminder.vue'
    import Bar from '@/components/Bar_new.vue';
    
    import { useIncentivesStore } from '@/stores/incentives'
    const incentivesStore = useIncentivesStore();

    import { useUserStore } from '@/stores/user';
    const userStore = useUserStore();

    import { useRatingsStore } from '@/stores/ratings'
    const ratingsStore = useRatingsStore();

    import userAvatar from '@/components/userAvatar.vue'
    import EmptyState from '@/components/EmptyState.vue'
    import PreviewHeader from '@/components/PreviewHeader.vue'

    // Lightweight Tooltip (local implementation – no global dependency)
    const activeTooltip = ref(null); // { x,y, title, lines:[], breakdown?:[] }
    // Feature flag: tooltips can be globally disabled without removing implementation
    const tooltipsEnabled = ref(true); // activate hover tooltips
    const showTooltip = ref(false);
    // tooltipData.breakdown (optional) only for salesTeams entries: array of { name, salesEuro, salesEuroNew, share }
    const tooltipData = ref({ title:'', lines:[], breakdown: null, sort:'salesEuro' });
    const tooltipPos = ref({ x:0, y:0 });
    let hideTimer = null;
    const tooltipLocked = ref(false);
    const AUTO_LOCK_ON_HOVER = false;

    const tooltipClasses = computed(() => ({
        ['theme-' + (userStore.selectedTheme || 'light')]: true,
        locked: tooltipLocked.value,
    }));

    function toggleLock(){
        tooltipLocked.value = !tooltipLocked.value;
    }

    function cycleSort(){
        if(!tooltipData.value.breakdown) return;
        tooltipData.value.sort = tooltipData.value.sort === 'salesEuro' ? 'salesEuroNew' : 'salesEuro';
        applySort();
    }

    function applySort(){
        if(!tooltipData.value.breakdown) return;
        const key = tooltipData.value.sort;
        tooltipData.value.breakdown.sort((a,b)=> (b[key]||0) - (a[key]||0));
        // recompute rank & cumulative
        let cumulative = 0;
        const total = key === 'salesEuro' ? tooltipData.value.breakdown.reduce((s,a)=> s + a.salesEuro,0) : tooltipData.value.breakdown.reduce((s,a)=> s + a.salesEuroNew,0);
        tooltipData.value.breakdown.forEach((m,i)=> { m.rank = i+1; cumulative += (key==='salesEuro'? m.salesEuro : m.salesEuroNew); m.cumShare = total>0 ? cumulative/total*100 : 0; });
    }

    function formatNumber(v){ return (v||v===0) ? v.toLocaleString('de-DE') : '—'; }

    // Preview-like meta/disclaimer computed values adapted to full view
    const display = computed(()=> userStore.currentSelection.incentives.display);
    const updatedDate = computed(()=> {
        const disp = display.value;
        const raw = incentivesStore?.incentive?.[disp]?.dateUpdated || incentivesStore?.incentive?.salesAgents?.dateUpdated;
        return raw ? raw.split(', ')[0] : '—';
    });
    const incentiveName = computed(()=> {
        const disp = display.value;
        return incentivesStore.incentive?.[disp]?.name || incentivesStore.incentive?.salesAgents?.name || '';
    });
    const incentiveDateRange = computed(()=> {
        const disp = display.value;
        const s = incentivesStore.incentive?.[disp]?.dateStart?.split(', ')[0] || incentivesStore.incentive?.salesAgents?.dateStart?.split(', ')[0];
        const e = incentivesStore.incentive?.[disp]?.dateEnd?.split(', ')[0] || incentivesStore.incentive?.salesAgents?.dateEnd?.split(', ')[0];
        return (s && e) ? `${s} – ${e}` : '';
    });
    const incentiveHasMeta = computed(()=> !!incentiveName.value || !!incentiveDateRange.value);
    const disclaimerText = computed(()=> {
        const disp = display.value;
        const text = incentivesStore?.incentive?.[disp]?.disclaimer;
        return text || 'Kein Hinweis verfügbar.';
    });

    // Cache for team breakdowns to avoid recomputation while hovering multiple agents of same team
    const teamBreakdownCache = new Map(); // key: teamId, value: { breakdown, totals }

    function resolveTeamId(obj){ return obj?.idTeam ?? obj?.idSalesTeam ?? null; }

    function buildChampionTooltip(entry, display){
        if(!entry) return { title:'', lines:[], breakdown:null };
        const title = (display==='salesAgents') ? entry.salesAgentName : entry.salesTeamName;
        const lines = [];
        if(entry.rankLastImport!=null) lines.push(`Rang: ${entry.rankLastImport}`);
        if(entry.salesEuro!=null) lines.push(`Umsatz: € ${formatNumber(entry.salesEuro)}`);
        if(entry.salesEuroNew!=null) lines.push(`Neu: € ${formatNumber(entry.salesEuroNew)}`);
        // New salesAchievements structure
        const bonusVal = entry?.salesAchievements?.bonus?.value ?? entry?.salesBonus ?? 0;
        const bonusEntitled = entry?.salesAchievements?.bonus?.entitled ?? entry?.salesBonusEntitled ?? null;
        if(bonusVal>0) lines.push(`Bonus: € ${formatNumber(bonusVal)}`);
        if(bonusEntitled === false && bonusVal>0) lines.push('Bonus: (nicht anspruchsberechtigt)');
        const cashBoxVal = entry?.salesAchievements?.cashBox?.value ?? entry?.salesTeamFund ?? 0;
        const cashBoxEntitled = entry?.salesAchievements?.cashBox?.entitled ?? null;
        if(display==='salesTeams' && cashBoxVal>0) lines.push(`Teamkasse: € ${formatNumber(cashBoxVal)}`);
        if(display==='salesTeams' && cashBoxEntitled === false && cashBoxVal>0) lines.push('Teamkasse: (nicht anspruchsberechtigt)');
        if(entry.isRookie===1) lines.push('Rookie');
        if(entry.salesAgentStatusCurrent) lines.push(`Status: ${entry.salesAgentStatusCurrent}`);

        // salesAgents: add share within team if possible
        let breakdown = null;
        if(display === 'salesAgents') {
            const teamId = resolveTeamId(entry);
            if(teamId){
                // ensure team breakdown exists (compute lazily from full salesAgents champion list)
                ensureTeamBreakdown(teamId);
                const cached = teamBreakdownCache.get(teamId);
                if(cached?.totals?.salesEuroTotal > 0){
                    const share = (entry.salesEuro / cached.totals.salesEuroTotal) * 100;
                    if(!isNaN(share)) lines.push(`Team-Anteil: ${share.toFixed(1)}%`);
                }
            }
        }

        // salesTeams: provide breakdown of its members
        if(display === 'salesTeams') {
            const teamId = resolveTeamId(entry);
            if(teamId){
                ensureTeamBreakdown(teamId);
                const cached = teamBreakdownCache.get(teamId);
                if(cached){
                    breakdown = cached.breakdown;
                    // Add summary line for clarity
                    if(cached.totals.salesEuroTotal != null){
                        lines.push(`Team Gesamt: € ${formatNumber(cached.totals.salesEuroTotal)}${cached.totals.salesEuroNewTotal? ' | Neu: € ' + formatNumber(cached.totals.salesEuroNewTotal):''}`);
                    }
                }
            }
        }
        return { title, lines, breakdown };
    }

    function ensureTeamBreakdown(teamId){
        if(teamBreakdownCache.has(teamId)) return;
        // Attempt to read all salesAgents champions from incentivesStore to assemble breakdown
        const allAgents = incentivesStore?.incentive?.salesAgents?.champions || [];
        const members = allAgents.filter(a => (a.idTeam ?? a.idSalesTeam) == teamId);
        if(!members.length){
            teamBreakdownCache.set(teamId, { breakdown: [], totals: { salesEuroTotal: 0, salesEuroNewTotal:0 } });
            return;
        }
        const salesEuroTotal = members.reduce((s,a)=> s + (a.salesEuro || 0), 0);
        const salesEuroNewTotal = members.reduce((s,a)=> s + (a.salesEuroNew || 0), 0);
        const breakdown = members
            .map(m => ({
                name: m.salesAgentName,
                idUser: m.idUser,
                avatar: m.idUser ? `https://sma-sellwerk.fra1.cdn.digitaloceanspaces.com/user/user_${m.idUser}.jpg` : '/avatars/dummy.webp',
                isRookie: m.isRookie,
                salesEuro: m.salesEuro || 0,
                salesEuroNew: m.salesEuroNew || 0,
                share: salesEuroTotal>0 ? (m.salesEuro / salesEuroTotal * 100) : 0,
                shareNew: salesEuroNewTotal>0 ? (m.salesEuroNew / salesEuroNewTotal * 100) : 0,
            }))
            .sort((a,b)=> b.salesEuro - a.salesEuro)
            .map((m,idx)=> ({ ...m, rank: idx+1 }));
        teamBreakdownCache.set(teamId, { breakdown, totals: { salesEuroTotal, salesEuroNewTotal } });
    }

    function onAvatarEnter(event, entry, display){
        if(!tooltipsEnabled.value) return;
        if(hideTimer){ clearTimeout(hideTimer); hideTimer=null; }
        if(!tooltipLocked.value){
            tooltipData.value = buildChampionTooltip(entry, display);
            positionTooltip(event);
            showTooltip.value = true;
            // initialize cumulative shares
            if(tooltipData.value.breakdown){
               let running = 0; const total = tooltipData.value.breakdown.reduce((s,a)=> s + a.salesEuro,0);
               tooltipData.value.breakdown.forEach(m=> { running += m.salesEuro; m.cumShare = total>0 ? running/total*100 : 0; });
            }
        }
    }
    function onAvatarMove(event){ if(!tooltipsEnabled.value) return; if(showTooltip.value && !tooltipLocked.value) positionTooltip(event); }
    // Provide a small grace period so user can move from avatar to tooltip without it vanishing
    function onAvatarLeave(){
        if(!tooltipsEnabled.value) return;
        if(tooltipLocked.value) return;
        if(hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(()=>{ if(!tooltipHovering.value && !tooltipLocked.value) showTooltip.value=false; }, 1100); // extended grace so man kann hinein fahren
    }

    function positionTooltip(evt){
        const pad=10;
        const vw = window.innerWidth;
        const vh = window.innerHeight;
        // kleiner Offset damit Maus schneller in Tooltip gelangt
        let x = evt.clientX + 6;
        let y = evt.clientY + 6;
        const estW = 320; const estH = 180; // etwas größer für bessere Randprüfung
        if(x + estW > vw - pad) x = vw - estW - pad;
        if(y + estH > vh - pad) y = vh - estH - pad;
        if(!tooltipLocked.value) tooltipPos.value = { x, y };
    }

    function top3Share(list){
        if(!list || !list.length) return 0;
        const total = list.reduce((s,a)=> s + a.salesEuro,0);
        const top3 = list.slice(0,3).reduce((s,a)=> s + a.salesEuro,0);
        return total>0 ? (top3/total*100) : 0;
    }
    function avgUmsatz(list){
        if(!list || !list.length) return '0';
        const total = list.reduce((s,a)=> s + a.salesEuro,0);
        return formatNumber(Math.round(total / list.length));
    }

    // Tooltip container event handlers (avoid inline complex expressions in template)
    const tooltipHovering = ref(false);
    function onTooltipContainerEnter(){
        if(!tooltipsEnabled.value) return;
        tooltipHovering.value = true;
        if(hideTimer) clearTimeout(hideTimer);
        // auto-lock on hover removed per request
    }
    function onTooltipContainerLeave(){
        if(!tooltipsEnabled.value) return;
        tooltipHovering.value = false;
        if(!tooltipLocked.value){
            setTimeout(()=> { if(!tooltipHovering.value && !tooltipLocked.value) showTooltip.value=false; }, 140);
        }
    }
    function closeTooltip(){
        if(!tooltipsEnabled.value) return;
        showTooltip.value = false; tooltipLocked.value = false;
    }


    function formatEuroValues(value) {
        return value.toLocaleString('de-DE');
    }

    // Helpers for new salesAchievements structure
    function bonusValue(entry){ return entry?.salesAchievements?.bonus?.value ?? entry?.salesBonus ?? 0; }
    function bonusEntitled(entry){ return entry?.salesAchievements?.bonus?.entitled ?? entry?.salesBonusEntitled ?? false; }
    function cashBoxValue(entry){ return entry?.salesAchievements?.cashBox?.value ?? entry?.salesTeamFund ?? 0; }
    function cashBoxEntitled(entry){ return entry?.salesAchievements?.cashBox?.entitled ?? false; }
    
    async function handleDisplayStatus(display) {
      await userStore.selectDisplayStatus('incentives', display);
    }

    // function formattedSalesAgentName(name) {
    // return name
    //     .toLowerCase() // Konvertiere in Kleinbuchstaben
    //     .replace(/ä/g, 'ae') // Ersetze ä durch ae
    //     .replace(/ö/g, 'oe') // Ersetze ö durch oe
    //     .replace(/ü/g, 'ue') // Ersetze ü durch ue
    //     .replace(/ß/g, 'ss') // Ersetze ß durch ss
    //     .replace(/\s+/g, '-') // Ersetze Leerzeichen durch -
    //     + '.webp'; // Füge die Dateiendung hinzu
    // };

    const handleImageError = (event) => {
        if(!/\/avatars\/dummy\.webp$/.test(event.target.src)) {
            event.target.src = '/avatars/dummy.webp';
        }
    };

    // unified podium image src (salesAgents & salesTeams). Teams currently don't have a dedicated idUser image -> fallback dummy
    const podiumImageSrc = (entry, display) => {
        if(!entry) return '/avatars/dummy.webp';
        const id = entry.idUser;
        if(!id) return '/avatars/dummy.webp';
        // Neuer Proxy-Pfad für Avatare
        return `/avatar/user_${id}.jpg`;
    };
    const podiumImageAlt = (display) => display === 'salesAgents' ? 'Profilbild Sales Manager' : 'Profilbild Area';

    const tableWrapper1 = ref(null);
    const tableWrapper2 = ref(null);
    const tableWrapper3 = ref(null);

    // onMounted(() => {
    //     if (tableWrapper1.value) {
    //         // console.log('tableWrapper1:', tableWrapper1.value); // Sollte das DOM-Element ausgeben
    //         // console.log('tableWrapper1.scrollLeft:', tableWrapper1.value[0].scrollLeft); // Sollte den aktuellen scrollLeft-Wert ausgeben
    //     } else {
    //         // console.error('tableWrapper1 ist nicht definiert');
    //     }

    //     if (tableWrapper2.value) {
    //         // console.log('tableWrapper2:', tableWrapper2.value); // Sollte das DOM-Element ausgeben
    //         // console.log('tableWrapper2.scrollLeft:', tableWrapper2.value[0].scrollLeft); // Sollte den aktuellen scrollLeft-Wert ausgeben
    //     } else {
    //         // console.error('tableWrapper2 ist nicht definiert');
    //     }
    // });

    const syncScroll = (table) => {
        if (table === 'table1' && tableWrapper1.value && tableWrapper2.value && tableWrapper3.value) {
            tableWrapper2.value[0].scrollLeft = tableWrapper1.value[0].scrollLeft;
            tableWrapper3.value[0].scrollLeft = tableWrapper1.value[0].scrollLeft;
        } else if (table === 'table2' && tableWrapper1.value && tableWrapper2.value && tableWrapper3.value) {
            tableWrapper1.value[0].scrollLeft = tableWrapper2.value[0].scrollLeft;
            tableWrapper3.value[0].scrollLeft = tableWrapper2.value[0].scrollLeft;
        } else if (table === 'table3' && tableWrapper1.value && tableWrapper2.value && tableWrapper3.value) {
            tableWrapper1.value[0].scrollLeft = tableWrapper3.value[0].scrollLeft;
            tableWrapper2.value[0].scrollLeft = tableWrapper3.value[0].scrollLeft;
        }
    };
    
    const glitterSpans = ref([])
    function generateRandomSpans() {
        const numberOfSpans = Math.floor(Math.random() * 21) + 20 // 20-40 spans
        const spans = []

        for (let i = 0; i < numberOfSpans; i++) {
            const size = Math.random() * 5 + .1 // Random size between 5px and 15px
            const top = Math.random() * -60 + 10 // Random top position between 0% and -100%
            const left = Math.random() * 100 // Random left position between 0% and 100%
            const duration = Math.random() * 10 + 2.5 // Random duration between .5s and 1.5s
            const delay = Math.random() * 5 // Random delay between 0s and 5s

            spans.push({
                size: `${size}px`,
                top: `${top}%`,
                left: `${left}%`,
                animationDuration: `${duration}s`,
                animationDelay: `${delay}s`
            })
        }

        glitterSpans.value = spans
    }

    onMounted(() => {
        generateRandomSpans()
    })

    // function getStatusDate(dateUpdated) {
    //     if (!dateUpdated) return '--';
    //     if (typeof dateUpdated === 'string') {
    //         // Falls schon im Format "DD.MM.YYYY, HH:mm:ss"
    //         const [datePart, timePart] = dateUpdated.split(', ');
    //         if (datePart && timePart) {
    //             return `${datePart}, ${timePart}`; // Datum und Uhrzeit
    //         }
    //         // Sonst als Date parsen und formatieren
    //         const dateObj = new Date(dateUpdated);
    //         if (!isNaN(dateObj)) {
    //             return dateObj.toLocaleString('de-DE', {
    //                 day: '2-digit',
    //                 month: '2-digit',
    //                 year: 'numeric',
    //                 hour: '2-digit',
    //                 minute: '2-digit',
    //                 second: '2-digit'
    //             });
    //         }
    //     } else {
    //         const dateObj = new Date(dateUpdated);
    //         if (!isNaN(dateObj)) {
    //             return dateObj.toLocaleString('de-DE', {
    //                 day: '2-digit',
    //                 month: '2-digit',
    //                 year: 'numeric',
    //                 hour: '2-digit',
    //                 minute: '2-digit',
    //                 second: '2-digit'
    //             });
    //         }
    //     }
    //     return '--';
    // }

    // // UpdateColors
    // function getStatusPercentage(dateUpdated) {
    //     if (!dateUpdated) return 100; // Fallback, falls kein Datum vorhanden
    
    //     // Falls das Datum im Format "DD.MM.YYYY, HH:mm:ss" kommt:
    //     let dateObj;
    //     if (typeof dateUpdated === 'string') {
    //         // Beispiel: "02.07.2025, 00:00:00"
    //         const [datePart, timePart] = dateUpdated.split(', ');
    //         if (datePart && timePart) {
    //             const [day, month, year] = datePart.split('.');
    //             dateObj = new Date(`${year}-${month}-${day}T${timePart}`);
    //         } else {
    //             dateObj = new Date(dateUpdated); // fallback für andere Formate
    //         }
    //     } else {
    //         dateObj = new Date(dateUpdated);
    //     }
    
    //     if (isNaN(dateObj)) return 100; // Fallback, falls ungültiges Datum
    
    //     const now = new Date();
    //     const adjusted = new Date(dateObj.getTime() + 14 * 60 * 60 * 1000);
    //     const diffHours = (now - adjusted) / (1000 * 60 * 60);
    //     // Max 24h Differenz (danach = 100%)
    //     const capped = Math.min(diffHours, 24);
    //     return Math.round((capped / 24) * 100); // 0–100%
    // }

    // const statusPercentage = computed(() => {
    //     const value = nextUpdateCurrentValue.value;
    //     const goal = nextUpdateGoalValue.value;
    //     if (value >= goal) return 0; // Nach Ziel wieder grün
    //     return Math.round((value / goal) * 100);
    // });
    // // Dynamischer Farbverlauf: grün → gelb → rot
    //     const statusStyle = computed(() => {
    //     const percentage = statusPercentage.value / 100; // 0 = grün, 1 = rot
    
    //     // Exakte Farben als RGB
    //     const green = { r: 47,  g: 172, b: 106 }; // #2FAC6A
    //     const orange = { r: 239, g: 125, b: 0 };  // #EF7D00
    //     const red = { r: 199, g: 20,  b: 24 };    // #C71418

    //     let r, g, b;
    //     if (percentage <= 0) {
    //         r = green.r; g = green.g; b = green.b;
    //     } else if (percentage < 0.5) {
    //         // Grün → Orange
    //         const t = percentage / 0.5;
    //         r = Math.round(green.r + (orange.r - green.r) * t);
    //         g = Math.round(green.g + (orange.g - green.g) * t);
    //         b = Math.round(green.b + (orange.b - green.b) * t);
    //     } else if (percentage < 1) {
    //         // Orange → Rot
    //         const t = (percentage - 0.5) / 0.5;
    //         r = Math.round(orange.r + (red.r - orange.r) * t);
    //         g = Math.round(orange.g + (red.g - orange.g) * t);
    //         b = Math.round(orange.b + (red.b - orange.b) * t);
    //     } else {
    //         r = red.r; g = red.g; b = red.b;
    //     }
    //     const bgColor = `rgb(${r}, ${g}, ${b})`;
    
    //     return {
    //         background: bgColor,
    //         // color: 'white',
    //         // padding: '8px',
    //         // borderRadius: '8px',
    //         // fontWeight: 'bold',
    //         // textAlign: 'center'
    //     };
    // });

    //     function getNextUpdateDistance() {
    //     // Hole das letzte Update-Datum inkl. Uhrzeit
    //     const prev = getPrevUpdateDate();
    //     if (!prev) return '--';
    
    //     // Nächster Werktag, gleiche Uhrzeit wie prev
    //     let next = new Date(prev);
    //     next.setDate(prev.getDate() + 1);
    //     while (next.getDay() === 0 || next.getDay() === 6) {
    //         next.setDate(next.getDate() + 1);
    //     }
    //     next.setHours(prev.getHours(), prev.getMinutes(), prev.getSeconds(), 0);
    
    //     const now = new Date();
    //     const diffMs = next - now;
    //     if (diffMs < 0) return '0 Std, 0 Min';
    
    //     const diffH = Math.floor(diffMs / (1000 * 60 * 60));
    //     const diffM = Math.floor((diffMs / (1000 * 60)) % 60);
    //     const diffD = Math.floor(diffMs / (1000 * 60 * 60 * 24));
    //     if (diffD > 0) {
    //         return `${diffD} Tag(e), ${diffH % 24} Std, ${diffM} Min`;
    //     }
    //     return `${diffH} Std, ${diffM} Min`;
    // }
    
    // const nextUpdateDistance = ref(getNextUpdateDistance());
    // let interval;
    // onMounted(() => {
    //     interval = setInterval(() => {
    //         nextUpdateDistance.value = getNextUpdateDistance();
    //     }, 1000 * 60); // jede Minute aktualisieren
    // });
    // onUnmounted(() => clearInterval(interval));

    // // function getNextUpdateMinutes() {
    // //     const now = new Date();
    // //     let next = new Date(now);
    
    // //     // Setze Uhrzeit auf 14:30:00
    // //     next.setHours(13, 34, 0, 0);
    
    // //     // Wenn heute schon nach 14:30, gehe auf nächsten Tag
    // //     if (now > next) {
    // //         next.setDate(next.getDate() + 1);
    // //     }
    
    // //     // Überspringe Wochenende (Sa=6, So=0)
    // //     while (next.getDay() === 0 || next.getDay() === 6) {
    // //         next.setDate(next.getDate() + 1);
    // //     }
    
    // //     // Minuten bis zum nächsten Update
    // //     return Math.round((next - now) / (1000 * 60));
    // // }
    
    // // function getMaxUpdateMinutes() {
    // //     const now = new Date();
    // //     let next = new Date(now);
    // //     next.setHours(14, 30, 0, 0);
    
    // //     // Wenn jetzt nach 14:30, nächstes Update ist morgen (oder nächster Werktag)
    // //     if (now > next) {
    // //         next.setDate(next.getDate() + 1);
    // //     }
    // //     // Überspringe Wochenende
    // //     while (next.getDay() === 0 || next.getDay() === 6) {
    // //         next.setDate(next.getDate() + 1);
    // //     }
    
    // //     // Letztes Update: Wenn jetzt vor 14:30, dann heute 14:30, sonst letzter Werktag 14:30
    // //     let prev = new Date(next);
    // //     prev.setDate(next.getDate() - 1);
    // //     while (prev.getDay() === 0 || prev.getDay() === 6) {
    // //         prev.setDate(prev.getDate() - 1);
    // //     }
    // //     prev.setHours(14, 30, 0, 0);
    
    // //     // Sonderfall: Wenn jetzt vor 14:30, dann ist prev heute 14:30
    // //     if (now < next && now < new Date(now.setHours(14, 30, 0, 0))) {
    // //         prev = new Date(now);
    // //         prev.setHours(14, 30, 0, 0);
    // //     }
    
    // //     return Math.round((next - prev) / (1000 * 60));
    // // }
    
    //     function parseDateTime(dateUpdated) {
    //     if (!dateUpdated) return null;
    //     if (typeof dateUpdated === 'string') {
    //         // Format: "DD.MM.YYYY, HH:mm:ss"
    //         const [datePart, timePart] = dateUpdated.split(', ');
    //         if (datePart && timePart) {
    //             const [day, month, year] = datePart.split('.');
    //             return new Date(`${year}-${month}-${day}T${timePart}`);
    //         }
    //         return new Date(dateUpdated);
    //     }
    //     return new Date(dateUpdated);
    // }
    
    // function getPrevUpdateDate() {
    //     // Hole das letzte Update-Datum aus dem Store
    //     return parseDateTime(incentivesStore?.incentive?.salesAgents?.dateUpdated);
    // }
    
    // function getNextUpdateDate() {
    //     const prev = getPrevUpdateDate();
    //     if (!prev) return null;
    //     let next = new Date(prev);
    //     next.setDate(prev.getDate() + 1);
    
    //     // Überspringe Wochenende vorwärts
    //     while (next.getDay() === 0 || next.getDay() === 6) {
    //         next.setDate(next.getDate() + 1);
    //     }
    //     // Uhrzeit bleibt gleich wie im letzten Update!
    //     next.setHours(prev.getHours(), prev.getMinutes(), prev.getSeconds(), 0);
    //     return next;
    // }
    
    // function getCurrentValueMinutes() {
    //     const now = new Date();
    //     const prev = getPrevUpdateDate();
    //     const next = getNextUpdateDate();
    //     if (!prev || !next) return 0;
    //     // Maximal bis zum Zielzeitpunkt zählen
    //     return Math.min(Math.max(0, Math.round((now - prev) / (1000 * 60))), getGoalValueMinutes());
    // }
    
    // function getGoalValueMinutes() {
    //     const prev = getPrevUpdateDate();
    //     const next = getNextUpdateDate();
    //     if (!prev || !next) return 0;
    //     return Math.round((next - prev) / (1000 * 60));
    // }
    
    // const nextUpdateCurrentValue = ref(getCurrentValueMinutes());
    // const nextUpdateGoalValue = ref(getGoalValueMinutes());
    
    // let updateInterval;
    //     onMounted(() => {
    //     updateInterval = setInterval(async () => {
    //         const value = getCurrentValueMinutes();
    //         const goal = getGoalValueMinutes();
    //         if (
    //             value >= goal &&
    //             !isToday(incentivesStore?.incentive?.salesAgents?.dateUpdated)
    //         ) {
    //             await incentivesStore.refresh();
    //             return;
    //         }
    //         nextUpdateCurrentValue.value = value;
    //         nextUpdateGoalValue.value = goal;
    //     }, 1000 * 60);
    // });
    // onUnmounted(() => clearInterval(updateInterval));

    // function isToday(dateUpdated) {
    //     if (!dateUpdated) return false;
    //     let dateObj;
    //     if (typeof dateUpdated === 'string') {
    //         const [datePart, timePart] = dateUpdated.split(', ');
    //         if (datePart && timePart) {
    //             const [day, month, year] = datePart.split('.');
    //             dateObj = new Date(`${year}-${month}-${day}T${timePart}`);
    //         } else {
    //             dateObj = new Date(dateUpdated);
    //         }
    //     } else {
    //         dateObj = new Date(dateUpdated);
    //     }
    //     const today = new Date();
    //     return dateObj.getDate() === today.getDate() &&
    //            dateObj.getMonth() === today.getMonth() &&
    //            dateObj.getFullYear() === today.getFullYear();
    // }


    //////
    
    // const debugDate = ref('01.07.2025, 00:00:00')
    // function startDebugDateAnimation() {
    //     const start = new Date('2025-07-01T00:00:00')
    //     const end = new Date('2025-07-03T14:30:00')
    //     const duration = 15000 // 5 Sekunden für einen Zyklus
    
    //     let frame
    //     function update() {
    //         const now = Date.now()
    //         const cycle = now % duration
    //         const percent = cycle / duration
    //         const ms = start.getTime() + (end.getTime() - start.getTime()) * percent
    //         const current = new Date(ms)
    //         debugDate.value = current.toLocaleString('de-DE', {
    //             day: '2-digit',
    //             month: '2-digit',
    //             year: 'numeric',
    //             hour: '2-digit',
    //             minute: '2-digit',
    //             second: '2-digit'
    //         })
    //         frame = requestAnimationFrame(update)
    //     }
    //     update()
    //     return () => cancelAnimationFrame(frame)
    // }
    
    // let stopDebug
    // onMounted(() => {
    //     stopDebug = startDebugDateAnimation()
    // })
    // onUnmounted(() => {
    //     if (stopDebug) stopDebug()
    // })

import InlineSpinner from '@/components/InlineSpinner.vue';
</script>

<template>
    <div class="incentives-root-wrapper">
        <div class="reminder-wrapper singlepage-wrapper incentive max-width">
            <main id="container-main">
                <preview-header
                    title="Champions"
                    :to="{ name: 'Dashboard', hash: '#champions' }"
                    button-label="Zurück zum Dashboard"
                    arrow="left"
                />
                <div v-if="userStore.currentSelection.incentives.isLoading === true">
                    <div class="widget-wrapper">
                        <div class="container">
                            <InlineSpinner size="lg" label="Daten werden geladen" />
                            <div class="loadingDescription">Daten werden geladen</div>
                        </div>
                    </div>
                </div>
                <div v-else-if="incentivesStore.incentives.length === 0" class="container">
                    <div class="widget-wrapper">
                        <div class="container">
                            <EmptyState tKey="no_data" icon="target" />
                        </div>
                    </div>
                </div>
                <div v-else v-for="display in ['salesAgents','salesTeams']" :key="display">
                    <div v-if="userStore.selectedMedia==='desktop' || userStore.currentSelection.incentives.display===display" :class="['widget-wrapper champions',  {'desktopView' : userStore.selectedMedia==='desktop'}]">
                        <div class="container-wrapper">
                            <template v-if="incentivesStore.incentiveSales[display].length > 0">        
                                <div class="container toggle-me">
                                    <div class="winners-podium">
                                        <div :class="['winner-wrapper', {'highlighted' : entry.highlightedPosition === true}]" v-for="(entry, index) in incentivesStore.topThree[display]" :key="entry.idUser || entry.idSalesAgent || index">
                                            <div :class="['winner', index === 0 ? '-second' : index === 1 ? '-first' : '-third', { 'logged-in': entry.highlightedPosition }]">
                                                <div id="glitterWrapper" class="glitter-wrapper" v-if="index === 1">
                                                    <span 
                                                        v-for="(span, index) in glitterSpans" 
                                                        :key="index" 
                                                        :style="{ width: span.size, height: span.size, top: span.top, left: span.left, position: 'absolute', animationDuration: span.animationDuration, animationDelay: span.animationDelay }"
                                                        class="glitter-span">
                                                    </span>
                                                </div>
                                                    <img
                                                        v-if="display === 'salesAgents' || display === 'salesTeams'"
                                                        :src="podiumImageSrc(entry, display)"
                                                        :alt="podiumImageAlt(display)"
                                                        @error="handleImageError"
                                                        @mouseenter="e=>onAvatarEnter(e, entry, display)"
                                                        @mousemove="onAvatarMove"
                                                        @mouseleave="onAvatarLeave"
                                                    />
                                                <div class="rookie" v-if="entry.isRookie === 1"><img class="rookie-icon" src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                                <!-- <template v-if="formatEuroValues(entry.salesEuroNew) > 0">
                                                    <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                    <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                    <div class="rookie" v-if="entry.isRookie === 1"><img class="rookie-icon" src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                                </template>
                                                <img v-else :src="'avatars/dummy.webp'" alt="Profilbild"> -->
                                            </div>
                                            <div class="txt-wrapper">
                                                <span v-if="display === 'salesAgents'">{{ entry.salesAgentName }}</span>
                                                <span v-if="display === 'salesTeams'">{{ entry.salesTeamName }}</span>
                                                <span v-if="incentivesStore.incentive[display].focus === 'salesEuroNew'">{{ formatEuroValues(entry.salesEuroNew) }}€</span>
                                                <span v-if="incentivesStore.incentive[display].focus === 'salesEuro'">{{ formatEuroValues(entry.salesEuro) }}€</span>
                                                <span v-if="entry.rankLastImport !== null" class="rank">{{ entry.rankLastImport }}</span>
                                                <!-- <template v-if="formatEuroValues(entry.salesEuroNew)  > 0">
                                                    <span v-if="display === 'salesAgents'">{{ entry.salesAgentName }}</span>
                                                    <span v-if="display === 'salesTeams'">{{ entry.salesTeamName }}</span>
                                                    <span v-if="incentivesStore.incentive[display].focus === 'salesEuroNew'">{{ formatEuroValues(entry.salesEuroNew) }}€</span>
                                                    <span v-if="incentivesStore.incentive[display].focus === 'salesEuro'">{{ formatEuroValues(entry.salesEuro) }}€</span>
                                                    <span v-if="entry.rankLastImport !== null" class="rank">{{ entry.rankLastImport }}</span>
                                                </template>
                                                    <template v-else>
                                                        <span v-if="display === 'salesAgents'">--</span>
                                                        <span v-if="display === 'salesTeams'">--</span>
                                                        <span v-if="incentivesStore.incentive[display].focus === 'salesEuroNew'">0€</span>
                                                        <span v-if="incentivesStore.incentive[display].focus === 'salesEuro'">0€</span>
                                                        <span v-if="entry.rankLastImport !== null" class="rank">{{ entry.rankLastImport }}</span>
                                                </template> -->
                                            </div>
                                        </div>
                                    </div>
                                    <div v-if="incentiveHasMeta" class="incentive-meta-bar" role="group" aria-label="Incentive Zeitraum">
                                        <div class="meta-left" v-if="incentiveName">{{ incentiveName }}</div>
                                        <div class="meta-right" v-if="incentiveDateRange"><span class="range-pill">{{ incentiveDateRange }}</span></div>
                                    </div>
                                    <div class="ranklist-wrapper">
                                        <h3 class="section-title ranking">Ranking</h3>
                                        <div class="ranklist" ref="tableWrapper1" @scroll="syncScroll('table1')">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Rang</th>
                                                       <th v-if="display === 'salesAgents'">Sales Manager</th>
                                                        <th v-if="display === 'salesTeams'">Areas</th>
                                                        <template v-if="incentivesStore.incentive[display].focus==='salesEuroNew'">
                                                            <th>Neu-Kd.</th>
                                                            <th>Umsatz</th>
                                                        </template>
                                                        <template v-if="incentivesStore.incentive[display].focus==='salesEuro'">
                                                            <th>Umsatz</th>
                                                            <th>Neu-Kd.</th>
                                                        </template>

                                                        <template v-if="userStore.currentSelection.scope ==='salesAgent'">
                                                            <th v-if="display==='salesAgents'">Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                                            <th v-if="display==='salesTeams'">Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                                        </template>
                                                        <template v-else-if="display==='salesTeams'">
                                                            <th>Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                                            <th>AM Bonus</th>
                                                        </template>
                                                        <template v-else>
                                                            <th>Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                                        </template>
                                                    </tr>
                                                </thead>
                                                    <tbody v-for="(entry, index) in incentivesStore.incentiveSales[display]" :key="entry.id">
                                                        <!-- <tr :class="{ 'logged-in': entry.highlightedPosition, 'first' : entry.rankLastImport === 1, 'second' : entry.rankLastImport === 2, 'third' : entry.rankLastImport === 3 }"> -->
                                                        <template v-if="userStore.currentSelection.scope === 'salesAgent'">
                                                            <!-- Separator for first bonus = 0 (salesAgents view) -->
                                                            <tr v-if="index === incentivesStore.incentiveSales[display].findIndex(a => a.salesAchievements?.bonus?.value === 0) && display === 'salesAgents'" class="separator">
                                                                <td v-for="i in [1,2,3,4,5]" :key="i"><span></span></td>
                                                            </tr>
                                                            <!-- Separator for first cashBox = 0 (salesTeams view) -->
                                                            <tr v-if="index === incentivesStore.incentiveSales[display].findIndex(a => a.salesAchievements?.cashBox?.value === 0) && display === 'salesTeams'" class="separator">
                                                                <td v-for="i in [1,2,3,4,5]" :key="i"><span></span></td>
                                                            </tr>
                                                        </template>

                                                        <template v-if="userStore.currentSelection.scope !== 'salesAgent' && display === 'salesTeams'">
                                                            <tr v-if="index === incentivesStore.incentiveSales[display].findIndex((salesAgent) => salesAgent.salesTeamFund === 0)"  class="separator"> 
                                                                <td v-for="i in [1,2,3,4,5,6]" :key="i">
                                                                    <span></span>
                                                                </td>
                                                            </tr>
                                                        </template>
                                                        
                                                        <tr :class="{ 'logged-in': entry.highlightedPosition}">

                                                            <td class="position" :class="{'rank-out': entry.rankChangeDirection === 'out', 'rank-new': entry.rankChangeDirection === 'new', 'rank-up': entry.rankChangeCount > 0, 'rank-hold': entry.rankChangeCount === '0', 'rank-down': entry.rankChangeCount < 0}">
                                                                <template v-if="entry.rankChangeDirection === 'out'">ST</template>
                                                                <template v-else>{{ entry.rankLastImport.toString().padStart(2,'0') }}</template>
                                                                <span class="change-count" v-if="entry.rankChangeDirection === 'new'">neu</span>
                                                                <span class="change-count" v-if="entry.rankChangeDirection === 'out'">storno</span>
                                                                <span class="change-count" v-if="entry.rankChangeCount !== '0'">{{ entry.rankChangeCount }}</span>
                                                                <!-- <span class="highlightBorder left" v-if="entry.highlightedPosition === true || entry.rankLastImport === 1 || entry.rankLastImport === 2 || entry.rankLastImport === 3"></span> -->
                                                                <span class="highlightBorder left" v-if="entry.highlightedPosition === true"></span>

                                                            </td>
                                                            <td>
                                                                <!-- {{ entry.idTeam }} -->
                                                                <userAvatar
                                                                    :size="32"
                                                                    :showSalesAgent="true"
                                                                    :isSalesAgent="display === 'salesAgents'"
                                                                    :isSalesTeam="display === 'salesTeams'"
                                                                    :idUser="entry.idUser"
                                                                    
                                                                    @mouseenter="e=>onAvatarEnter(e, entry, display)"
                                                                    @mousemove="onAvatarMove"
                                                                    @mouseleave="onAvatarLeave"
                                                                />                                            
                                                                <div class="marquee" v-if="display==='salesAgents'">
                                                                <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                                <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                                                </div>
                                                                <div class="marquee" v-if="display==='salesTeams'">
                                                                    <p>{{ entry.salesTeamName }}</p>
                                                                    <!-- <p>{{ entry.salesTeamName }}</p> -->
                                                                </div>
                                                            </td>
                                                            <td v-if="false" class="sm-wrapper isSalesAgent">
                                                                <div
                                                                    :class="['rank', (entry.salesAgentStatusCurrent) ? entry.salesAgentStatusCurrent.toLowerCase().replaceAll(' ', '-') : '', 'rating-' + entry.rating,]">
                                                                    <p v-if="ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings" class="rating-value">{{ ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings }}</p>
                                                                    <p v-else class="rating-value">--</p>                                                                    <!-- <img src="@/assets/imgs/champions/dummy-avatar.png" alt=""> -->
                                                                    <!-- <userAvatar v-if="display === 'salesAgents'" :salesAgentId="entry.idUser" /> -->
                                                                    <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                    <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                    <div class="rookie" v-if="entry.isRookie === 1"><img src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                                                    <!-- <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + formattedSalesAgentName(entry.salesAgentName)" alt="">
                                                                    <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + formattedSalesAgentName(entry.salesTeamName)" alt=""> -->
                                                                    <div class="tooltip">
                                                                        <div class="sm-wrapper nav isSalesAgent">
                                                                            <div class="rank professional-silver rating-4">
                                                                                <p class="rating-value">{{ 4.3 }}</p>
                                                                                <!-- <img src="@/assets/imgs/champions/dummy-avatar.png" alt=""> -->
                                                                                <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                                <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                                <!-- <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + formattedSalesAgentName(entry.salesAgentName)" alt="">
                                                                                <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + formattedSalesAgentName(entry.salesTeamName)" alt=""> -->
                                                                            </div>
                                                                            <div class="txt-wrapper"> 
                                                                                <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                                                <!-- <p class="sm-lastName">{{userStore.status}}</p> -->
                                                                                <p class="sm-status">{{entry.salesAgentStatusCurrent}}</p>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                                <div class="marquee" v-if="display === 'salesAgents'">
                                                                    <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                                    <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                                                </div>
                                                                <div class="marquee" v-if="display === 'salesTeams'">
                                                                    <p>{{ entry.salesTeamName }}</p>
                                                                    <!-- <p>{{ entry.salesTeamName }}</p> -->
                                                                </div>
                                                            </td>
                                                            <template v-if="incentivesStore.incentive.salesAgents.focus==='salesEuroNew'">
                                                                <td>{{ formatEuroValues(entry.salesEuroNew) }}€</td>
                                                                <td>{{ formatEuroValues(entry.salesEuro) }}€</td>
                                                            </template>
                                                            <template v-if="incentivesStore.incentive.salesAgents.focus==='salesEuro'">
                                                                <td>{{ formatEuroValues(entry.salesEuro) }}€</td>
                                                                <td>{{ formatEuroValues(entry.salesEuroNew) }}€</td>
                                                            </template>
                                                            <template v-if="userStore.currentSelection.scope ==='salesAgent'">
                                                                <!-- Team view (show cashBox) when scope is salesAgent but display is salesTeams -->
                                                                <td v-if="display==='salesTeams'"
                                                                    :class="{ 'achieved': cashBoxEntitled(entry) && cashBoxValue(entry) > 0, 'not-achieved': !cashBoxEntitled(entry) && cashBoxValue(entry) > 0 }"
                                                                    :data-cashbox-value="cashBoxValue(entry)"
                                                                    :data-cashbox-entitled="cashBoxEntitled(entry)"
                                                                >
                                                                    {{ formatEuroValues(cashBoxValue(entry)) }}€
                                                                </td>
                                                                <!-- Agents view (show bonus) -->
                                                                <td v-if="display==='salesAgents'"
                                                                    :class="{ 'achieved': bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved': !bonusEntitled(entry) && bonusValue(entry) > 0 }"
                                                                    :data-bonus-value="bonusValue(entry)"
                                                                    :data-bonus-entitled="bonusEntitled(entry)"
                                                                >
                                                                    {{ formatEuroValues(bonusValue(entry)) }}€
                                                                </td>
                                                            </template>
                                                            <template v-else-if="display==='salesTeams'">
                                                                <!-- Teams view (not scope salesAgent) shows cashBox and bonus -->
                                                                <td
                                                                    :class="{ 'achieved': cashBoxEntitled(entry) && cashBoxValue(entry) > 0, 'not-achieved': !cashBoxEntitled(entry) && cashBoxValue(entry) > 0 }"
                                                                    :data-cashbox-value="cashBoxValue(entry)"
                                                                    :data-cashbox-entitled="cashBoxEntitled(entry)"
                                                                >
                                                                    {{ formatEuroValues(cashBoxValue(entry)) }}€
                                                                </td>
                                                                <td
                                                                    :class="{ 'achieved': bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved': !bonusEntitled(entry) && bonusValue(entry) > 0 }"
                                                                    :data-bonus-value="bonusValue(entry)"
                                                                    :data-bonus-entitled="bonusEntitled(entry)"
                                                                >
                                                                    {{ formatEuroValues(bonusValue(entry)) }}€
                                                                </td>
                                                            </template>
                                                            <template v-else>
                                                                <!-- Default: only bonus column -->
                                                                <td
                                                                    :class="{ 'achieved': bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved': !bonusEntitled(entry) && bonusValue(entry) > 0 }"
                                                                    :data-bonus-value="bonusValue(entry)"
                                                                    :data-bonus-entitled="bonusEntitled(entry)"
                                                                >
                                                                    {{ formatEuroValues(bonusValue(entry)) }}€
                                                                </td>
                                                            </template>
                                                        </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>
                                <div v-if="incentivesStore.incentiveNoSalesNew[display].length > 0" class="container out-of-rank">
                                    <h3 class="section-title out-of-rank-head">Teilnehmer ohne Neu-Kunden-Umsatz</h3>
                                    <div class="ranklist-wrapper">
                                        <div class="ranklist" ref="tableWrapper2" @scroll="syncScroll('table2')">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Rang</th>
                                                       <th v-if="display === 'salesAgents'">Sales Manager</th>
                                                        <th v-if="display === 'salesTeams'">Areas</th>
                                                        <template v-if="incentivesStore.incentive[display].focus==='salesEuroNew'">
                                                            <th>Neu-Kd.</th>
                                                            <th>Umsatz</th>
                                                        </template>
                                                        <template v-if="incentivesStore.incentive[display].focus==='salesEuro'">
                                                            <th>Umsatz</th>
                                                            <th>Neu-Kd.</th>
                                                        </template>

                                                        <template v-if="userStore.currentSelection.scope ==='salesAgent'">
                                                            <th v-if="display==='salesAgents'">Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                                            <th v-if="display==='salesTeams'">Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                                        </template>
                                                        <template v-else-if="display==='salesTeams'">
                                                            <th>Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                                            <th>AM Bonus</th>
                                                        </template>
                                                        <template v-else>
                                                            <th>Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                                        </template>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr :class="{ 'logged-in': entry.highlightedPosition }"
                                                        v-for="(entry, index) in incentivesStore.incentiveNoSalesNew[display]" :key="index">
                                                        <td class="position" :class="{'rank-out': entry.rankChangeDirection === 'out', 'rank-new': entry.rankChangeDirection === 'new', 'rank-up': entry.rankChangeCount > 0, 'rank-hold': entry.rankChangeCount === '0', 'rank-down': entry.rankChangeCount < 0}">
                                                            <template v-if="entry.rankChangeDirection === 'out'">ST</template>
                                                            <template v-else>{{ entry.rankLastImport.toString().padStart(2,'0') }}</template>
                                                            <span class="change-count" v-if="entry.rankChangeDirection === 'new'">neu</span>
                                                            <span class="change-count" v-if="entry.rankChangeDirection === 'out'">storno</span>
                                                            <span class="change-count" v-if="entry.rankChangeCount !== '0'">{{ entry.rankChangeCount }}</span>
                                                            <!-- <span class="highlightBorder left" v-if="entry.highlightedPosition === true || entry.rankLastImport === 1 || entry.rankLastImport === 2 || entry.rankLastImport === 3"></span> -->
                                                            <span class="highlightBorder left" v-if="entry.highlightedPosition === true"></span>

                                                        </td>
                                                        <td>
                                                            <!-- {{ entry.idTeam }} -->
                                                            <userAvatar
                                                                :size="32"
                                                                :showSalesAgent="true"
                                                                :isSalesAgent="display === 'salesAgents'"
                                                                :isSalesTeam="display === 'salesTeams'"
                                                                :idUser="entry.idUser"
                                                                
                                                                @mouseenter="e=>onAvatarEnter(e, entry, display)"
                                                                @mousemove="onAvatarMove"
                                                                @mouseleave="onAvatarLeave"
                                                            />                                            
                                                            <div class="marquee" v-if="display==='salesAgents'">
                                                            <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                            <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                                            </div>
                                                            <div class="marquee" v-if="display==='salesTeams'">
                                                                <p>{{ entry.salesTeamName }}</p>
                                                                <!-- <p>{{ entry.salesTeamName }}</p> -->
                                                            </div>
                                                        </td>
                                                        <td v-if="false" class="sm-wrapper isSalesAgent">
                                                            <div
                                                                :class="['rank', (entry.salesAgentStatusCurrent) ? entry.salesAgentStatusCurrent.toLowerCase().replaceAll(' ', '-') : '', 'rating-' + entry.rating]">
                                                                <p v-if="ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings" class="rating-value">{{ ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings }}</p>
                                                                <p v-else class="rating-value">--</p>                                                                <!-- <img src="@/assets/imgs/champions/dummy-avatar.png" alt=""> -->
                                                                <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                <div class="rookie" v-if="entry.isRookie === 1"><img src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                                                <!-- <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + formattedSalesAgentName(entry.salesAgentName)" alt="">
                                                                <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + formattedSalesAgentName(entry.salesTeamName)" alt=""> -->

                                                            </div>
                                                            <div class="marquee" v-if="display === 'salesAgents'">
                                                                <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                                <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                                            </div>
                                                            <div class="marquee" v-if="display === 'salesTeams'">
                                                                <p>{{ entry.salesTeamName }}</p>
                                                                <!-- <p>{{ entry.salesTeamName }}</p> -->
                                                            </div>
                                                        </td>
                                                        <td>{{ formatEuroValues(entry.salesEuroNew) }}€</td>
                                                        <td>{{ formatEuroValues(entry.salesEuro) }}€</td>
                                                        <td
                                                            :class="{ 'achieved': bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved': !bonusEntitled(entry) && bonusValue(entry) > 0 }"
                                                            :data-bonus-value="bonusValue(entry)"
                                                            :data-bonus-entitled="bonusEntitled(entry)"
                                                        >
                                                            {{ formatEuroValues(bonusValue(entry)) }}€
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <!-- <hr> -->
                                    <!-- <p class="info"><sup>*1</sup>{{ incentivesStore.incentive[display].disclaimer }}</p> -->
                                </div>
                                <div v-if="incentivesStore.incentiveNoSales[display].length > 0" class="container out-of-rank">
                                    <h3 class="section-title out-of-rank-head">Teilnehmer ohne Umsatz</h3>
                                    <div class="ranklist-wrapper">
                                        <div class="ranklist" ref="tableWrapper3" @scroll="syncScroll('table3')">
                                            <table>
                                                <thead>
                                                    <tr>
                                                        <th>Rang</th>
                                                       <th v-if="display === 'salesAgents'">Sales Manager</th>
                                                        <th v-if="display === 'salesTeams'">Areas</th>
                                                        <template v-if="incentivesStore.incentive[display].focus==='salesEuroNew'">
                                                            <th>Neu-Kd.</th>
                                                            <th>Umsatz</th>
                                                        </template>
                                                        <template v-if="incentivesStore.incentive[display].focus==='salesEuro'">
                                                            <th>Umsatz</th>
                                                            <th>Neu-Kd.</th>
                                                        </template>

                                                        <template v-if="userStore.currentSelection.scope ==='salesAgent'">
                                                            <th v-if="display==='salesAgents'">Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                                            <th v-if="display==='salesTeams'">Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                                        </template>
                                                        <template v-else-if="display==='salesTeams'">
                                                            <th>Teamkasse<span class="asterisk">&#42;&sup2;</span></th>
                                                            <th>AM Bonus</th>
                                                        </template>
                                                        <template v-else>
                                                            <th>Bonus<span class="asterisk">&#42;&sup1;</span></th>
                                                        </template>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr :class="{ 'logged-in': entry.highlightedPosition }"
                                                        v-for="(entry, index) in incentivesStore.incentiveNoSales[display]" :key="index">
                                                        <td class="position"
                                                            :class="{ 'rank-out': entry.rankChangeDirection === 'out', 'rank-up': entry.rankChangeCount > 0, 'rank-hold': entry.rankChangeCount === 0, 'rank-down': entry.rankChangeCount < 0 }">
                                                            <template v-if="entry.rankChangeDirection === 'out'">ST</template>
                                                            <template v-else>--</template>
                                                            <span class="change-count"
                                                                v-if="entry.rankChangeDirection === 'new'">neu</span>
                                                            <span class="change-count"
                                                                v-if="entry.rankChangeDirection === 'out'">storno</span>
                                                            <span class="change-count" v-else>{{ entry.rankChangeCount }}</span>
                                                            <span class="highlightBorder left" v-if="entry.highlightedPosition === true"></span>
                                                        </td>
                                                        <td>
                                                            <!-- {{ entry.idTeam }} -->
                                                            <userAvatar
                                                                :size="32"
                                                                :showSalesAgent="true"
                                                                :isSalesAgent="display === 'salesAgents'"
                                                                :isSalesTeam="display === 'salesTeams'"
                                                                :idUser="entry.idUser"
                                                                
                                                                @mouseenter="e=>onAvatarEnter(e, entry, display)"
                                                                @mousemove="onAvatarMove"
                                                                @mouseleave="onAvatarLeave"
                                                            />                                            
                                                            <div class="marquee" v-if="display==='salesAgents'">
                                                            <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                            <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                                            </div>
                                                            <div class="marquee" v-if="display==='salesTeams'">
                                                                <p>{{ entry.salesTeamName }}</p>
                                                                <!-- <p>{{ entry.salesTeamName }}</p> -->
                                                            </div>
                                                        </td>
                                                        <td v-if="false" class="sm-wrapper isSalesAgent">
                                                            <div
                                                                :class="['rank', (entry.salesAgentStatusCurrent) ? entry.salesAgentStatusCurrent.toLowerCase().replaceAll(' ', '-') : '', 'rating-' + entry.rating]">
                                                                <p v-if="ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings" class="rating-value">{{ ratingsStore.rating?.week?.bySalesAgent?.find((salesAgent) => salesAgent.idUser === entry.idUser )?.average.ratings }}</p>
                                                                <p v-else class="rating-value">--</p>                                                                <!-- <img src="@/assets/imgs/champions/dummy-avatar.png" alt=""> -->
                                                                <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + entry.photo + '.webp'" alt="Profilbild">
                                                                <div class="rookie" v-if="entry.isRookie === 1"><img src="@/assets/imgs/icons/chicken-with-egg.png" alt="Rookie"></div>
                                                                <!-- <img @error="handleImageError" v-if="display === 'salesAgents'" :src="'avatars/' + formattedSalesAgentName(entry.salesAgentName)" alt="">
                                                                <img @error="handleImageError" v-if="display === 'salesTeams'" :src="'avatars/' + formattedSalesAgentName(entry.salesTeamName)" alt=""> -->

                                                            </div>
                                                            <div class="marquee" v-if="display === 'salesAgents'">
                                                                <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p>
                                                                <!-- <p>{{ entry.salesAgentName }} [{{ entry.salesTeamName }}]</p> -->
                                                            </div>
                                                            <div class="marquee" v-if="display === 'salesTeams'">
                                                                <p>{{ entry.salesTeamName }}</p>
                                                                <!-- <p>{{ entry.salesTeamName }}</p> -->
                                                            </div>
                                                        </td>
                                                        <td>{{ formatEuroValues(entry.salesEuroNew) }}€</td>
                                                        <td>{{ formatEuroValues(entry.salesEuro) }}€</td>
                                                        <td
                                                            :class="{ 'achieved': bonusEntitled(entry) && bonusValue(entry) > 0, 'not-achieved': !bonusEntitled(entry) && bonusValue(entry) > 0 }"
                                                            :data-bonus-value="bonusValue(entry)"
                                                            :data-bonus-entitled="bonusEntitled(entry)"
                                                        >
                                                            {{ formatEuroValues(bonusValue(entry)) }}€
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                    <!-- <hr> -->
                                    <!-- <p class="info"><sup>*1</sup>{{ incentivesStore.incentive[display].disclaimer }}</p> -->
                                </div>
                                <!-- Removed bottom footer disclaimer to avoid redundancy -->
                                 <!-- Preview-like footer: Update & Hinweise in own container under main table -->
                                    <div class="container">
                                        <div class="update-info compact" role="note" aria-labelledby="incentivesInfoHeading">
                                            <h6 id="incentivesInfoHeading" class="info-heading">Stand: <time :datetime="(display==='salesTeams' ? incentivesStore?.incentive?.salesTeams?.dateUpdated : incentivesStore?.incentive?.salesAgents?.dateUpdated)?.split(', ')[0]">{{ (display==='salesTeams' ? incentivesStore?.incentive?.salesTeams?.dateUpdated : incentivesStore?.incentive?.salesAgents?.dateUpdated)?.split(', ')[0] || '—' }}</time></h6>
                                            <div class="disclaimer" role="note" aria-label="Hinweis">
                                                <p id="incentiveDisclaimer" class="disclaimer-text">
                                                    <span v-if="display === 'salesAgents'" class="asterisk-mark"><span aria-hidden="true">*</span><sup>1</sup></span>
                                                    <span v-if="display === 'salesTeams'" class="asterisk-mark"><span aria-hidden="true">*</span><sup>2</sup></span>
                                                    {{ (display === 'salesTeams' ? incentivesStore?.incentive?.salesTeams?.disclaimer : incentivesStore?.incentive?.salesAgents?.disclaimer) || 'Kein Hinweis verfügbar.' }}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                            </template>
                            <div v-else class="container">
                                <div class="warning">Noch keinen Umsatz geschrieben!</div>
                            </div>
                        </div>
                        <div class='wave-wrapper'>
                            <div class='wave -one'></div>
                            <div class='wave -two'></div>
                            <div class='wave -three'></div>
                        </div>
                        <!-- TODO: skip the toggle wrapper on desktop -->
                        <div v-if="userStore.selectedMedia!=='desktop'" class="toggle-wrapper">
                            <div class="toggle-option" @click="handleDisplayStatus('salesAgents')">
                                <span class="toggle-label">SalesManager</span>
                                <label class="switch">
                                    <input type="radio" name="champions-toggle" value="salesAgents"
                                        :checked="userStore.currentSelection.incentives.display === 'salesAgents'">
                                    <span class="custom-radio"></span>
                                </label>
                            </div>
                            <div class="toggle-option" @click="handleDisplayStatus('salesTeams')">
                                <span class="toggle-label">Areas</span>
                                <label class="switch">
                                    <input type="radio" name="champions-toggle" value="salesTeams"
                                        :checked="userStore.currentSelection.incentives.display==='salesTeams'">
                                    <span class="custom-radio"></span>
                                </label>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
            <reminder></reminder>
        </div>
           <div v-if="tooltipsEnabled && showTooltip && tooltipData.title"
               class="incentive-tooltip styled"
               :class="tooltipClasses"
               :style="{ left: tooltipPos.x + 'px', top: tooltipPos.y + 'px', borderRadius: '16px', boxShadow: '0 12px 28px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12)', backdropFilter: 'saturate(140%) blur(8px)', padding: '12px 14px' }"
             @mouseenter="onTooltipContainerEnter"
             @mouseleave="onTooltipContainerLeave">
            <div class="tooltip-bar">
                <h4>{{ tooltipData.title }}</h4>
                <div class="actions">
                    <button class="btn-icon" @click.stop="closeTooltip" title="Schließen">×</button>
                    <button class="btn-icon" @click.stop="cycleSort" :title="'Sortierung: ' + (tooltipData.sort==='salesEuro' ? 'Umsatz' : 'Neu-Umsatz')">{{ tooltipData.sort==='salesEuro' ? '€' : 'Neu' }}</button>
                    <button class="btn-icon" @click.stop="toggleLock" :title="tooltipLocked ? 'Tooltip entsperren' : 'Tooltip fixieren'">{{ tooltipLocked ? '🔓' : '🔒' }}</button>
                </div>
            </div>
            <ul class="meta-lines simple-stats" v-if="!tooltipData.breakdown || tooltipData.breakdown.length===0">
                <li v-for="(l,i) in tooltipData.lines" :key="i" class="stat-line">{{ l }}</li>
            </ul>
            <div v-if="(!tooltipData.breakdown || tooltipData.breakdown.length===0) && tooltipData.teamShare != null" class="team-share">
                <div class="share-label">TEAM-ANTEIL</div>
                <div class="share-bar">
                    <span class="bar total" :style="{ width: Number(tooltipData.teamShare).toFixed(1) + '%', background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #22c55e 100%)' }"></span>
                </div>
                <div class="share-value">{{ Number(tooltipData.teamShare).toFixed(1) }}%</div>
            </div>
            <div v-if="tooltipData.breakdown && tooltipData.breakdown.length" class="team-breakdown">
                <div class="tb-head">
                    <span class="col-rank" title="Rang"><span class="pill pill-muted">#</span></span>
                    <span class="col-avatar" title="Avatar"></span>
                    <span class="col-name">Sales Manager</span>
                    <span class="col-euro" :class="[{active: tooltipData.sort==='salesEuro'}, 'as-pill']" title="Umsatz (klicken zum Sortieren)"><span class="pill" :class="tooltipData.sort==='salesEuro' ? 'pill-accent' : 'pill-muted'">€</span></span>
                    <span class="col-new" :class="[{active: tooltipData.sort==='salesEuroNew'}, 'as-pill']" title="Neu-Umsatz (klicken zum Sortieren)"><span class="pill" :class="tooltipData.sort==='salesEuroNew' ? 'pill-accent' : 'pill-muted'">Neu</span></span>
                    <span class="col-share" title="Anteil %"><span class="pill pill-muted">%</span></span>
                    <span class="col-cum" title="Kumuliert %"><span class="pill pill-muted">Cum</span></span>
                </div>
                <div class="tb-body">
                    <div v-for="m in tooltipData.breakdown" :key="m.name" class="tb-row" :class="{ 'is-top': m.rank <=3 }">
                        <span class="col-rank"><span class="pill pill-muted">{{ m.rank }}</span></span>
                        <span class="col-avatar"><span class="avatar-wrapper"><img :src="m.avatar" @error="handleImageError" alt="" /><span v-if="m.isRookie==1" class="rookie-badge" title="Rookie"></span></span></span>
                        <span class="col-name">{{ m.name }}</span>
                        <span class="col-euro"><span class="pill pill-soft">{{ formatNumber(m.salesEuro) }}</span></span>
                        <span class="col-new"><span class="pill pill-soft">{{ formatNumber(m.salesEuroNew) }}</span></span>
                        <span class="col-share"><span class="pill pill-muted">{{ m.share.toFixed(1) }}</span></span>
                        <span class="col-cum"><span class="pill pill-muted">{{ m.cumShare ? m.cumShare.toFixed(1) : '—' }}</span></span>
                        <div class="bar-line">
                            <span class="bar total" :style="{ width: m.share.toFixed(1) + '%', background: 'linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #22c55e 100%)' }"></span>
                            <span class="bar new" :style="{ width: m.shareNew.toFixed(1) + '%', background: 'linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%)' }"></span>
                        </div>
                    </div>
                </div>
                <div class="tb-footer" v-if="tooltipData.breakdown.length">
                    <span class="foot-label">Top 3 Anteil</span>
                    <span class="foot-value">{{ top3Share(tooltipData.breakdown).toFixed(1) }}%</span>
                    <span class="foot-label">Ø Umsatz</span>
                    <span class="foot-value">€ {{ avgUmsatz(tooltipData.breakdown) }}</span>
                </div>
            </div>
        </div>
    </div>
</template>

<style scoped>
/* Modern tooltip look and pill system */
:deep(.incentive-tooltip) {
    border-radius: 16px;
    box-shadow: 0 12px 28px rgba(0,0,0,0.18), 0 2px 6px rgba(0,0,0,0.12);
    backdrop-filter: saturate(140%) blur(8px);
    padding: 12px 14px;
    position: fixed;
    z-index: 9999;
    pointer-events: auto;
}
:deep(.incentive-tooltip) .tooltip-bar h4 {
    font-weight: 600;
}

.pill {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    border-radius: 999px;
    padding: 4px 10px;
    font-size: 12px;
    line-height: 1;
    border: 1px solid transparent;
}
.pill-soft {
    background: rgba(127, 127, 127, 0.12);
    color: inherit;
}
.pill-muted {
    background: rgba(127, 127, 127, 0.08);
    color: inherit;
}
.pill-accent {
    background: var(--accent-bg, #3b82f6);
    color: #fff;
}
.theme-dark .pill-soft { background: rgba(255,255,255,0.08); }
.theme-dark .pill-muted { background: rgba(255,255,255,0.06); }

.meta-lines {
    display: flex;
    flex-wrap: wrap;
    gap: 6px;
    margin: 8px 0 10px;
}
.meta-lines .pill { margin: 0; }

.simple-stats {
    display: grid;
    grid-template-columns: 1fr;
    gap: 6px;
}
.simple-stats .stat-line {
    background: rgba(127,127,127,0.08);
    border-radius: 8px;
    padding: 6px 10px;
    font-size: 13px;
}
.theme-dark .simple-stats .stat-line { background: rgba(255,255,255,0.06); }

.team-share { margin-top: 8px; }
.team-share .share-label { font-size: 12px; opacity: 0.7; margin-bottom: 4px; }
.team-share .share-bar { width: 100%; height: 6px; background: rgba(127,127,127,0.15); border-radius: 999px; overflow: hidden; }
.theme-dark .team-share .share-bar { background: rgba(255,255,255,0.12); }
.team-share .share-bar .bar.total { display: block; height: 100%; border-radius: 999px; background: linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #22c55e 100%) !important; }
.team-share .share-value { margin-top: 4px; font-weight: 600; font-size: 12px; }

.team-breakdown .tb-head {
    display: grid;
    grid-template-columns: 28px 36px 1fr 80px 80px 64px 64px;
    align-items: center;
    gap: 8px;
}
.team-breakdown .tb-head .as-pill .pill { padding: 2px 8px; font-weight: 600; }

.team-breakdown .tb-body .tb-row {
    display: grid;
    grid-template-columns: 28px 36px 1fr 80px 80px 64px 64px;
    align-items: center;
    gap: 8px;
    padding: 6px 0;
}
.team-breakdown .tb-body .tb-row .pill { padding: 2px 8px; }

.team-breakdown .tb-body .tb-row .bar-line {
    grid-column: 1 / -1;
    height: 4px;
    display: grid;
    grid-template-columns: 1fr;
    margin-top: 6px;
}
:deep(.team-breakdown) .tb-body .tb-row .bar-line .bar.total {
    /* Team Anteil gradient: 0% red → 50% orange → 100% green */
    background: linear-gradient(90deg, #ef4444 0%, #f59e0b 50%, #22c55e 100%) !important;
    border-radius: 999px;
    height: 4px;
    display: block;
}
:deep(.team-breakdown) .tb-body .tb-row .bar-line .bar.new {
    background: linear-gradient(90deg, #60a5fa 0%, #3b82f6 100%) !important;
    border-radius: 999px;
    height: 4px;
    margin-top: 2px;
    display: block;
}

.team-breakdown .tb-footer {
    display: grid;
    grid-template-columns: auto 1fr auto 1fr;
    gap: 8px;
    align-items: center;
    margin-top: 10px;
}
.team-breakdown .tb-footer .foot-label { opacity: 0.7; }
.team-breakdown .tb-footer .foot-value { font-weight: 600; }
</style>

<style scoped>
/* optional subline styling */
.incentives-preview-block .subline { margin:-.2rem 0 .2rem; }
.incentives-preview-block .context-line { margin:0; font-size:.55rem; opacity:.65; letter-spacing:.35px; }

/* Modern info box */
.update-info { display:flex; flex-direction:column; gap:.45rem; border-radius:12px; background:#1e252c; font-size:.75rem; letter-spacing:.32px; }
.update-info.compact { background:linear-gradient(145deg,#1c2329,#222b33); }
.theme--light .update-info { background:#ffffff; border-color:#d5dde5; }
.info-heading { margin:0; font-size:.6rem; font-weight:600; letter-spacing:.5px; display:flex; gap:.35rem; align-items:baseline; }
.info-heading time { font-weight:600; font-size:.62rem; }
.disclaimer { margin:0; }
.disclaimer summary { list-style:none; cursor:pointer; display:inline-flex; align-items:center; gap:.35rem; font-weight:600; letter-spacing:.5px; font-size:.52rem; padding:.18rem .42rem .2rem; border-radius:999px; background:#1f262c; border:1px solid #2a333c; transition:.2s background,.2s border-color,.2s color; width:fit-content; }
.disclaimer summary:hover { background:#262e35; border-color:#34414c; }
.disclaimer[open] summary { background:#28313a; border-color:#3a4752; }
.chevron { display:inline-block; font-size:.5rem; line-height:1; transform:rotate(0deg); transition:.25s transform; }
.chevron.open { transform:rotate(180deg); }
.theme--light .disclaimer summary { background:#f5f7fa; border-color:#d8e1e8; }
.theme--light .disclaimer[open] summary { background:#edf2f6; border-color:#ccd5dd; }
.disclaimer-text { line-height:1.45; font-size:.6rem; max-width: 100%; }
.disclaimer-preview { margin:0; font-size:.56rem; opacity:.75; line-height:1.35; }
.line-clamp { display:-webkit-box; -webkit-line-clamp:2; -webkit-box-orient:vertical; overflow:hidden; line-clamp:2; }
.asterisk { color:var(--sellwerk-color-secondary,#36b4e8); font-weight:700; }
.theme--light .asterisk { color:var(--sellwerk-color-main,#1677c5); }
.disclaimer summary::-webkit-details-marker { display:none; }
/* Footnotes under disclaimer */
.footnotes { margin-top:.35rem; }
.footnote { margin:0; font-size:.54rem; opacity:.75; line-height:1.35; }
.asterisk-mark { font-weight:700; color:var(--sellwerk-color-secondary,#36b4e8); font-size: .75rem;}
.theme--light .asterisk-mark { color:var(--sellwerk-color-main,#1677c5); }
.asterisk-mark sup { font-size:.65em; vertical-align:super; }
/* Incentive context pill */
.incentive-context { margin:-.2rem 0 .5rem; }
.incentive-context .context-line { margin:0; display:flex; flex-wrap:wrap; gap:.6rem; align-items:center; font-size:.75rem; letter-spacing:.45px; }
.incentive-context .label { background:var(--sellwerk-color-secondary,#36b4e8); color:#fff; padding:.26rem .65rem .3rem; border-radius:10px; font-size:.56rem; font-weight:600; letter-spacing:.5px; }
.theme--light .incentive-context .label { background:var(--sellwerk-color-main,#1677c5); }
.incentive-context .range { opacity:.7; font-weight:500; }
/* New meta bar above table */
.incentive-meta-bar { display:flex; justify-content:space-between; align-items:center; padding:.5rem .75rem .55rem; margin:0 0 .5rem; background:linear-gradient(145deg,#1c2329,#222b33); border:1px solid #2d3944; border-radius:12px; font-size:.6rem; letter-spacing:.48px; }
.theme--light .incentive-meta-bar { background:#ffffff; border-color:#d5dde5; }
.meta-left { font-weight:600; letter-spacing:.55px; display:flex; align-items:center; gap:.5rem; font-size:.62rem; }
.meta-right { display:flex; align-items:center; }
.range-pill { background:var(--sellwerk-color-secondary,#36b4e8); color:#fff; font-weight:600; letter-spacing:.5px; padding:.28rem .75rem .32rem; border-radius:999px; font-size:.75rem; box-shadow:0 2px 4px -2px rgba(0,0,0,.5); }
.theme--light .range-pill { background:var(--sellwerk-color-main,#1677c5); }
/* Table modern styling */
.ranklist table { width:100%; border-collapse:separate; border-spacing:0 6px; font-size:.75rem; }
.ranklist thead tr { background:transparent; }
.ranklist thead th { text-align:left; font-weight:600; font-size:.75rem; letter-spacing:.5px; padding:.45rem .6rem; color:#cbd5e1; text-transform:uppercase; opacity:.88; }
.theme--light .ranklist thead th { color:#475569; opacity:.9; }
.ranklist tbody tr { background:#1d252c; border:1px solid #2d3540; transition:.18s background,.18s transform; }
.ranklist tbody tr.logged-in { background:linear-gradient(135deg,#22303a,#1d252c); border-color:#364854; box-shadow:0 0 0 1px rgba(255,255,255,.06) inset; }
.ranklist tbody tr:hover { background:#242e36; }
.theme--light .ranklist tbody tr { background:#ffffff; border-color:#d5dde5; }
.theme--light .ranklist tbody tr.logged-in { background:linear-gradient(135deg,#f1f7fb,#ffffff); border-color:#cdd7df; }
.theme--light .ranklist tbody tr:hover { background:#eff4f8; }
.ranklist tbody td { padding:.55rem .6rem; vertical-align:middle; font-size:.75rem; }
.ranklist tbody td.position { font-weight:600; letter-spacing:.48px; color:#e2e8f0; width:56px; font-size:.6rem; }
.theme--light .ranklist tbody td.position { color:#1e293b; }
.ranklist tbody td .change-count { display:inline-block; margin-left:.3rem; font-size:.55rem; font-weight:600; }
/* .ranklist tbody td.achieved { background:rgba(54,180,232,.08); } */
.ranklist tbody td.not-achieved { opacity:.6; }
/* .theme--light .ranklist tbody td.achieved { background:rgba(22,119,197,.10); } */
.ranklist tbody td .marquee p { margin:0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; max-width:220px; transition:.2s max-width; }
/* Allow full names on wider viewports */
@media (min-width: 1100px) {
    .ranklist tbody td .marquee p { white-space:normal; overflow:visible; text-overflow:clip; max-width:none; }
}
.ranklist tbody td .rank { font-weight:600; }
.ranklist .highlightBorder.left { position:absolute; left:0; top:0; bottom:0; width:3px; background:var(--sellwerk-color-secondary,#36b4e8); border-radius:3px 0 0 3px; }
.theme--light .ranklist .highlightBorder.left { background:var(--sellwerk-color-main,#1677c5); }

/* Incentive tooltip */
.incentive-tooltip { display: flex; flex-direction: column; position: fixed; z-index: 9999; padding: 12px 14px 12px; border-radius: 14px; font-size: 12px; line-height: 1.4; max-width: 460px; min-width:320px; box-shadow: 0 4px 18px -4px rgba(0,0,0,.28), 0 2px 7px -1px rgba(0,0,0,.18); backdrop-filter: blur(14px) saturate(160%); pointer-events: auto; animation: fadeInTooltip .12s ease-out; border:1px solid rgba(0,0,0,0.08); }
.incentive-tooltip:before { content:""; position:absolute; left:-24px; top:0; bottom:0; width:24px; }
.incentive-tooltip.locked { pointer-events:auto; cursor:default; }
.incentive-tooltip .tooltip-bar { display:flex;  align-items:center; justify-content:space-between; gap:8px; }
.incentive-tooltip .actions { display:flex; gap:6px; }
.incentive-tooltip .btn-icon { background:rgba(0,0,0,0.06); border:0; padding:4px 8px; border-radius:8px; font-size:11px; cursor:pointer; line-height:1; font-weight:600; }
.theme-dark .btn-icon { background:rgba(255,255,255,0.1); color:#f5f7f9; }
.theme-light .btn-icon { color:#222; }
.incentive-tooltip .btn-icon:hover { background:rgba(239,125,0,0.25); color:#000; }
.theme-dark .incentive-tooltip .btn-icon:hover { background:rgba(239,125,0,0.35); color:#fff; }
.incentive-tooltip.theme-dark { background: rgba(18,19,23,0.9); color:#f8f9fa; border-color: rgba(255,255,255,0.09); }
.incentive-tooltip.theme-light { background: rgba(255,255,255,0.9); color:#1e2228; border-color: rgba(0,0,0,0.08); }
.incentive-tooltip.theme-light .tb-row:nth-child(even){ background:rgba(0,0,0,0.04); }
.incentive-tooltip.theme-dark .tb-row:nth-child(even){ background:rgba(255,255,255,0.05); }
.incentive-tooltip h4 { margin:0 0 6px; font-size:13px; font-weight:600; letter-spacing:.4px; display:flex; align-items:center; gap:6px; }
.incentive-tooltip h4:before { content:''; display:inline-block; width:6px; height:6px; border-radius:50%; background:linear-gradient(135deg,#ef7d00,#ffb347); box-shadow:0 0 0 3px rgba(239,125,0,0.25); }
.incentive-tooltip ul { margin:0; padding:0; list-style:none; }
.incentive-tooltip li { margin:0; padding:0; }
.incentive-tooltip .team-breakdown { margin-top:10px; border-top:1px solid rgba(0,0,0,0.08); padding-top:10px; }
.theme-dark .team-breakdown { border-top-color: rgba(255,255,255,0.14); }
.team-breakdown .tb-head, .team-breakdown .tb-row { display:grid; grid-template-columns: 26px 34px 1fr 70px 60px 52px 54px; column-gap:6px; align-items:center; position:relative; }
.team-breakdown .tb-head { font-size:10px; text-transform:uppercase; letter-spacing:.55px; opacity:.7; font-weight:600; }
.team-breakdown .tb-body { max-height:200px; overflow-y:auto; margin-top:4px; }
.team-breakdown .tb-row { font-size:11px; padding:6px 4px 16px; border-radius:6px; line-height:1.25; }
.team-breakdown .tb-row.is-top { background:linear-gradient(90deg,rgba(239,125,0,0.15),rgba(239,125,0,0)); }
.theme-dark .team-breakdown .tb-row.is-top { background:linear-gradient(90deg,rgba(239,125,0,0.25),rgba(239,125,0,0)); }
.team-breakdown .col-avatar img { width:26px; height:26px; border-radius:50%; object-fit:cover; display:block; box-shadow:0 0 0 1px rgba(0,0,0,0.15); }
.team-breakdown .col-avatar .avatar-wrapper { position:relative; display:inline-block; }
.team-breakdown .col-avatar .rookie-badge { position:absolute; right:-4px; bottom:-4px; width:14px; height:14px; background:radial-gradient(circle at 30% 30%, #fff6c4, #f5b400); border:1px solid rgba(0,0,0,0.35); border-radius:50%; box-shadow:0 0 0 1px rgba(255,255,255,0.5), 0 1px 2px rgba(0,0,0,0.45); }
.theme-dark .team-breakdown .col-avatar .rookie-badge { border-color:rgba(255,255,255,0.25); box-shadow:0 0 0 1px rgba(0,0,0,0.55), 0 1px 2px rgba(0,0,0,0.65); }
.team-breakdown .col-avatar .rookie-badge:after { content:'R'; position:absolute; inset:0; display:flex; align-items:center; justify-content:center; font-size:9px; font-weight:700; color:#5a4200; font-family: system-ui, sans-serif; }
.theme-dark .team-breakdown .col-avatar img { box-shadow:0 0 0 1px rgba(255,255,255,0.15); }
.team-breakdown .tb-row .bar-line { position:absolute; left:0; right:0; bottom:4px; height:4px; background:rgba(0,0,0,0.06); border-radius:4px; overflow:hidden; }
.theme-dark .team-breakdown .tb-row .bar-line { background:rgba(255,255,255,0.08); }
.team-breakdown .tb-row .bar { position:absolute; left:0; top:0; bottom:0; background:linear-gradient(90deg,#ef7d00,#ffb347); opacity:.55; }
.team-breakdown .tb-row .bar.new { background:linear-gradient(90deg,#2fac6a,#64d38f); opacity:.85; mix-blend-mode:plus-lighter; }
.team-breakdown .col-euro, .team-breakdown .col-share, .team-breakdown .col-new, .team-breakdown .col-rank, .team-breakdown .col-cum { text-align:right; font-variant-numeric: tabular-nums; }
.team-breakdown .tb-head .col-euro.active, .team-breakdown .tb-head .col-new.active { color:#ef7d00; }
.team-breakdown .tb-head { position:sticky; top:0; backdrop-filter:blur(6px); }
.team-breakdown .tb-footer { display:grid; grid-template-columns: repeat(4, auto); gap:8px 16px; margin-top:8px; padding-top:8px; font-size:10.5px; border-top:1px solid rgba(0,0,0,0.08); }
.theme-dark .team-breakdown .tb-footer { border-top-color: rgba(255,255,255,0.14); }
.team-breakdown .tb-footer .foot-label { opacity:.65; font-weight:500; }
.team-breakdown .tb-footer .foot-value { font-weight:600; }
.team-breakdown .col-name { overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.team-breakdown .tb-body::-webkit-scrollbar { width:6px; }
.team-breakdown .tb-body::-webkit-scrollbar-track { background:transparent; }
.team-breakdown .tb-body::-webkit-scrollbar-thumb { background:rgba(0,0,0,0.25); border-radius:8px; }
.theme-dark .team-breakdown .tb-body::-webkit-scrollbar-thumb { background:rgba(255,255,255,0.25); }
@keyframes fadeInTooltip { from { opacity:0; transform:translateY(2px);} to { opacity:1; transform:translateY(0);} }
</style>

